import type { DatumEngine } from '@datum-sdk/engine';
import type { SkyboxSettings } from '@datum-sdk/scene-format';

/**
 * Skybox fade-in on the SDK path, public API only. The engine shows the sky
 * once the reveal completes and has no fade of its own (CR E1), so the scene is
 * loaded with the sky at intensity 0 (createSdkScene) and, once revealed,
 * `start()` raises `skybox.intensity` to the authored value on legacy's
 * smoothstep. A same-URL `setSceneEnvironment` patch only writes
 * `scene.backgroundIntensity` — no reload — but a patch REPLACES `skybox`
 * wholesale, so every write carries the full authored object: a missing
 * `url2k` / `rotation` / `blur` would change the source key and reload it.
 */
export const createSkyboxFade = (
  engine: DatumEngine,
  skybox: SkyboxSettings,
  durationMs: number,
) => {
  const target = skybox.intensity ?? 1;
  // The full objects, built once and written every frame with only `intensity`
  // changing — the engine clones what it is given.
  const patch: SkyboxSettings = { ...skybox, intensity: 0 };
  const environment = { skybox: patch };
  const patchMode = { mode: 'patch' } as const;
  let startMs = 0;
  let raf = 0;
  let started = false;

  const step = (now: number): void => {
    const t = Math.min(1, Math.max(0, (now - startMs) / durationMs));
    patch.intensity = target * t * t * (3 - 2 * t);
    engine.setSceneEnvironment(environment, patchMode);
    engine.requestRender();
    raf = t < 1 ? requestAnimationFrame(step) : 0;
  };

  return {
    start: (): void => {
      if (started) return;
      started = true;
      startMs = performance.now();
      raf = requestAnimationFrame(step);
    },
    dispose: (): void => {
      cancelAnimationFrame(raf);
      raf = 0;
    },
  };
};
